<template>
  <layout class="performance-page-container">
    <div class="w-full flex adm-container sm:flex-col lg:flex-row">
      <div class="search-input sm:w-full lg:w-1/2">
        <div class="w-full h-full flex items-center justify-center">
          <div class="w-full">
            <suggestion-input
              :list="driversList"
              :lookup-fields="['name', 'number']"
              accessor="name"
              placeholder="Search drivers by name or mobile number"
              icon-class="fa-search"
              icon-position="left"
              @autocomplete-item-set="onAutoCompleteItemSelected"
              @autocomplete-cleared="onAutoCompleteClear"
            >
              <div slot="noResult">No driver found</div>
            </suggestion-input>
          </div>
        </div>
      </div>
      <div class="flex items-center justify-end text-primary font-bold sm:w-full lg:w-1/4 sm:my-4 lg:my-0">
        <pt-datepicker
          :value="date"
          :range="weekRange"
          :week-range="weekRange"
          :show-next-month-dates="weekRange"
          :show-prev-month-dates="weekRange"
          :close-on-range-select="true"
          class="flex flex-col flex-1 sm:pl-0 lg:pl-4"
          @selected="setDate"
        >
          <div
            slot="afterDayPicker"
            class="p-4"
          >
            <pt-radio-group
              v-model="selectedDateRangeType"
              :options="dateSelectionType"
              name="dateSelection"
            />
          </div>
        </pt-datepicker>
      </div>
      <div class="flex items-center justify-end text-primary font-bold sm:w-full lg:w-1/4">
        <download-csv
          :download-url="csvUrl"
          file-name="Performance List"
        />
      </div>
    </div>
    <div class="w-full flex adm-container relative performance-grids-row z-0 sm:flex-col lg:flex-row">
      <div
        :class="{'group bg-primary-light text-white': selectedCard === 'Gross Fare'}"
        class="group h-full border border-grey-light rounded bg-white p-8 performance-grid-cell hover:bg-primary-light hover:text-white cursor-pointer z-0 sm:w-full lg:w-1/3 sm:my-4 lg:my-0"
        @click="setCard('Gross Fare')"
      >
        <div class="text-primary-dark group-hover:text-white font-semibold text-xl mb-4">GROSS FARE</div>
        <div class="flex items-center mb-8">
          <div class="text-4xl"> ৳ {{ summary.gross_fare }}</div>
          <div class="ml-4 uppercase">Total</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-24"> ৳ {{ summary.per_trip }}</div>
          <div class="flex items-center">Per Trip</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-24"> ৳ {{ summary.per_hour_online }}</div>
          <div class="flex items-center">Per Hour Online</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-24"> ৳ {{ summary.per_km_on_trip }}</div>
          <div class="flex items-center">Per KM On Trip</div>
        </div>
      </div>
      <div
        :class="{'group bg-primary-light text-white': selectedCard === 'Trips'}"
        class="group h-full border border-grey-light rounded bg-white p-8 z-0 performance-grid-cell hover:bg-primary-light hover:text-white cursor-pointer sm:w-full lg:w-1/3 lg:mx-4 sm:my-4 lg:my-0"
        @click="setCard('Trips')"
      >
        <div class="text-primary-dark group-hover:text-white font-semibold text-xl mb-4">TRIPS</div>
        <div class="flex items-center mb-8">
          <div class="text-4xl">{{ summary.total_trips }}</div>
          <div class="ml-4 uppercase">Total</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-24">{{ summary.hours_online }}</div>
          <div class="flex items-center">Hours Online</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-24">{{ summary.trips_per_hour }}</div>
          <div class="flex items-center">Trips Per Hour</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-24">{{ summary.distance_per_trip }} KM</div>
          <div class="flex items-center">Distance Per Trip</div>
        </div>
      </div>
      <div
        :class="{'group bg-primary-light text-white': selectedCard === 'Ratings'}"
        class="group h-full border border-grey-light rounded bg-white p-8 z-0 performance-grid-cell hover:bg-primary-light hover:text-white cursor-pointer sm:w-full lg:w-1/3 sm:my-4 lg:my-0"
        @click="setCard('Ratings')"
      >
        <div class="text-primary-dark group-hover:text-white text-xl mb-4 font-semibold">RATINGS</div>
        <div class="flex items-center mb-8">
          <div class="text-4xl">{{ summary.selected_time_rating }}</div>
          <div class="ml-4 uppercase">Selected Time</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-16">{{ summary.life_time_rating }}</div>
          <div class="flex items-center">Lifetime Rating</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-16">{{ summary.accptance_rate }} {{ percentageSign(summary.accptance_rate) }}</div>
          <div class="flex items-center">Acceptance Rate</div>
        </div>
        <div class="flex mb-2">
          <div class="font-bold w-16">{{ summary.cancellataion_rate }} {{ percentageSign(summary.cancellataion_rate) }}</div>
          <div class="flex items-center">Driver Cancellation Rate</div>
        </div>
      </div>
    </div>
    <div class="w-full flex flex-col adm-container z-0 relative">
      <div
        v-if="selectedCard !== null"
        class="mb-8"
      >
        <span class="text-2xl mr-8">{{ selectedCard }} Selected</span>
        <span
          class="text-lg font-bold text-primary cursor-pointer uppercase"
          @click="setCard()"
        >Show All Metrics</span>
      </div>
      <div class="table-loader-container relative">
        <pt-list
          v-if="chunkList.length"
          class="overflow-scroll mb-8">
          <div :class="{ 'bg-grey-lighter border-b flex-row font-bold sm:hidden': true, 'lg:inline-flex': !selectedCard, 'lg:flex': selectedCard }">
            <div
              class="lg:border-r pl-4 py-4"
              style="min-width: 250px">Driver</div>
            <div
              v-if="!selectedCard || selectedCard === 'Gross Fare'"
              class="lg:border-r text-center py-4 lg:flex-1"
              style="min-width: 600px">Gross Fare</div>
            <div
              v-if="!selectedCard || selectedCard === 'Trips'"
              class="lg:border-r text-center py-4 lg:flex-1"
              style="min-width: 590px">Trips</div>
            <div
              v-if="!selectedCard || selectedCard === 'Ratings'"
              class="text-center py-4 lg:flex-1"
              style="min-width: 600px">Ratings</div>
          </div>
          <div :class="{ 'bg-grey-lighter border-b flex-row font-bold sm:hidden': true, 'lg:inline-flex': !selectedCard, 'lg:flex': selectedCard }">
            <div
              class="lg:border-r pl-4 py-4 border-b"
              style="min-width: 250px">Name</div>
            <div
              v-if="!selectedCard || selectedCard === 'Gross Fare'"
              class="flex border-b lg:flex-1"
              style="min-width: 600px"
            >
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Total</div>
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Per Trip</div>
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Per Hour Online</div>
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Fare Per KM</div>
            </div>
            <div
              v-if="!selectedCard || selectedCard === 'Trips'"
              class="flex border-b lg:flex-1"
              style="min-width: 590px"
            >
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Total</div>
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Hours Online</div>
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Trips Per Hour</div>
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Distance Per Trip</div>
            </div>
            <div
              v-if="!selectedCard || selectedCard === 'Ratings'"
              class="flex border-b lg:flex-1"
              style="min-width: 600px"
            >
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Total</div>
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Lifetime</div>
              <div class="lg:border-r py-4 text-right pr-4 lg:flex-1">Acceptance Rate</div>
              <div class="py-4 text-right pr-4 lg:flex-1">Cancellation Rate</div>
            </div>
          </div>
          <pt-list-item
            v-for="(performanceRow, rowIndex) in chunkList"
            :key="rowIndex"
            class="inline-flex p-0 performance-table-row"
          >
            <div class="inline-flex flex-1 bg-white sm:flex-col lg:flex-row sm:pb-4 lg:pb-0">
              <div
                class="flex px-4 py-2 sm:flex-col lg:flex-row lg:border-r lg:pb-2"
                style="min-width: 250px">
                <router-link
                  :to="`/driver/${performanceRow.id}`"
                  class="flex no-underline cursor-pointer text-black hover:text-grey-darker items-center font-bold"
                >
                  <pt-avatar
                    :type="performanceRow['profile_pic'] ? 'image' : 'icon'"
                    :src="`https://cdn.pathao.com${performanceRow['profile_pic']}`"
                    size="nn"
                    style="width: 30px; height: 30px;"
                    class="mr-2"
                  />
                  <div class="flex-1">{{ performanceRow.name }}</div>
                </router-link>
              </div>
              <div
                v-if="!selectedCard || selectedCard === 'Gross Fare'"
                class="flex lg:items-center lg:flex-1 sm:flex-col lg:flex-row"
                style="min-width: 600px"
              >
                <div class="p-4 font-bold flex-1 lg:hidden">Gross Fare</div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Total:</span> ৳ {{ performanceRow.gross_fare }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Per Trip:</span> ৳ {{ performanceRow.per_trip }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Per Hour Online:</span> ৳ {{ performanceRow.per_hour_online }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Per KM On Trip:</span> ৳ {{ performanceRow.per_km_on_trip }}
                </div>
              </div>
              <div
                v-if="!selectedCard || selectedCard === 'Trips'"
                class="flex lg:items-center lg:flex-1 sm:flex-col lg:flex-row"
                style="min-width: 590px"
              >
                <div class="p-4 font-bold flex-1 lg:hidden">Trips</div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Total Trips:</span> {{ performanceRow.total_trips }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Hours Online:</span> {{ performanceRow.hours_online }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Trips Per Hour:</span> {{ performanceRow.trips_per_hour }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Distance Per Trip:</span> {{ performanceRow.distance_per_trip }} KM
                </div>
              </div>
              <div
                v-if="!selectedCard || selectedCard === 'Ratings'"
                class="flex lg:items-center lg:flex-1 sm:flex-col lg:flex-row"
                style="min-width: 600px"
              >
                <div class="p-4 font-bold flex-1 lg:hidden">Ratings</div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Selected Time Rating:</span> {{ performanceRow.selected_time_rating }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Lifetime Rating:</span> {{ performanceRow.life_time_rating }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4 lg:border-r">
                  <span class="mr-2 lg:hidden">Acceptance Rate:</span> {{ performanceRow.accptance_rate }} {{ percentageSign(performanceRow.accptance_rate) }}
                </div>
                <div class="h-full flex items-center px-4 lg:justify-end lg:flex-1 sm:py-1 lg:py-4">
                  <span class="mr-2 lg:hidden">Cancellation Rate:</span> {{ performanceRow.cancellataion_rate }} {{ percentageSign(performanceRow.cancellataion_rate) }}
                </div>
              </div>
            </div>
          </pt-list-item>
        </pt-list>

        <table-placeholder
          :is-syncing="isLoadingListData"
          :data-length="chunkList.length"
          loader-class="text-black"
        />
      </div>
      <pt-pagination
        :total-rows="totalRows"
        :current-page="currentPage"
        :page-size="pageSize"
        @page-changed="sliceListForPagination"
      />
    </div>
  </layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import clone from 'lodash/clone';
import find from 'lodash/find';
import orderBy from 'lodash/orderBy';
import { PtRadioGroup, PtRadio } from '@pathao/ui-components/esm/FormComponents';
import { PtDatepicker } from '@pathao/ui-components/esm/Datepicker';
import { PtViewTable } from '@pathao/ui-components/esm/Table';
import { PtPagination } from '@pathao/ui-components/esm/Pagination';
import { PtButton } from '@pathao/ui-components/esm/Button';
import { PtList, PtListItem } from '@components/custom/List';
import Config from '@config';
import PaginationConfig from '@config/pagination';
import PtAvatar from '@components/custom/Avatar';
import SuggestionInput from '@components/custom/SuggestionInput';
import TablePlaceholder from '@components/custom/TablePlaceholder';
import DownloadCsv from '@components/custom/DownloadCsv';
import Layout from '@layouts/main';
import makeRequest from '@utils/apiRequest';
import SliceData from '@utils/slice-data';
import SelectedDates from '@utils/selected-dates';

const PERFORMANCE_LIST_BASE_URL = `${Config.api.baseURL}api/partners/performance/list`;

export default {
  name: 'Performance',
  page: {
    title: 'Performance',
  },
  components: { PtDatepicker, PtViewTable, Layout, SuggestionInput, PtRadioGroup, PtRadio, PtPagination, PtButton, TablePlaceholder, DownloadCsv, PtList, PtListItem, PtAvatar },
  data() {
    return {
      date: new Date(),
      selectedDateRangeType: 'daily',
      dateSelectionType: [
        { value: 'daily', label: 'Daily' },
        { value: 'weekly', label: 'Weekly' },
      ],
      summary: {},
      list: [],
      chunkList: [],
      selectedCard: null,
      totalRows: 0,
      currentPage: PaginationConfig.CURRENT_PAGE,
      pageSize: PaginationConfig.PAGE_SIZE,
      isLoadingListData: false,
      filteredDriverId: null,
      sortKey: null,
      sortOrder: 'desc',
    };
  },
  computed: {
    ...mapGetters({
      driversList: 'drivers/drivers',
    }),
    weekRange() {
      return this.selectedDateRangeType === 'weekly';
    },
    csvUrl() {
      const baseUrl = `${PERFORMANCE_LIST_BASE_URL}/export`;
      const paramList = [];

      if (this.date) {
        const selectedDates = SelectedDates(this.date);
        paramList.push(`filterByStartDate=${selectedDates['filterByStartDate']}`);
        paramList.push(`filterByEndDate=${selectedDates['filterByEndDate']}`);
      }

      if (this.filteredDriverId) {
        paramList.push(`filterByDriverId=${this.filteredDriverId}`);
      }

      return paramList.length ? `${baseUrl}?${paramList.join('&')}` : baseUrl;
    },
  },
  watch: {
    selectedDateRangeType() {
      this.date = null;
    },
  },
  mounted() {
    this.fetchPerformanceSummary();
    this.fetchPerformanceList();
    this.fetchDriverList();
  },
  methods: {
    ...mapActions({
      fetchDriverList: 'drivers/fetchDrivers',
    }),
    fetchPerformanceSummary() {
      const selectedDates = SelectedDates(this.date);
      const performanceSummaryMeta = {
        url: `${Config.api.baseURL}api/partners/performance/summary`,
        params: {
          ...selectedDates,
        },
      };

      makeRequest(performanceSummaryMeta)
        .then((response) => {
          this.summary = response.data;
        })
        .catch((errorResponse) => {
          this.$notification.error('Error getting performance summary');
          console.error(errorResponse);
        });
    },
    fetchPerformanceList(page = PaginationConfig.CURRENT_PAGE, pageSize = PaginationConfig.PAGE_SIZE) {
      const selectedDates = SelectedDates(this.date);
      const performanceListMeta = {
        url: `${PERFORMANCE_LIST_BASE_URL}`,
        params: { ...selectedDates },
      };

      this.isLoadingListData = true;

      makeRequest(performanceListMeta)
        .then((response) => {
          this.list = response.data;
          this.sliceListForPagination();

          this.isLoadingListData = false;
        })
        .catch((errorResponse) => {
          this.isLoadingListData = false;
          this.$notification.error(`Error in getting drivers' performance information`);
          console.error(errorResponse);
        });
    },
    onAutoCompleteItemSelected(suggestionItem) {
      this.filteredDriverId = suggestionItem.id;
      this.sliceListForPagination();
    },
    onAutoCompleteClear() {
      this.filteredDriverId = null;
      this.sliceListForPagination();
    },
    setDate(date) {
      this.date = date;
      this.fetchPerformanceSummary();
      this.fetchPerformanceList();
    },
    setCard(cardName = null) {
      this.selectedCard = cardName && this.selectedCard !== cardName ? cardName : null;
      this.sliceListForPagination(this.currentPage, this.pageSize);
    },
    sliceListForPagination(currentPage = PaginationConfig.CURRENT_PAGE, pageSize = PaginationConfig.PAGE_SIZE) {
      this.sortList();
      const filteredList = this.getFilteredList();
      this.chunkList = SliceData(filteredList, currentPage, pageSize);
      this.totalRows = filteredList.length || 0;
      this.currentPage = currentPage;
      this.pageSize = pageSize;
    },
    getFilteredList() {
      let filteredList = [];

      if (this.filteredDriverId) {
        const filteredDriver = find(this.list, (currentDriver) => {
          return this.filteredDriverId === currentDriver.id;
        });

        filteredList = filteredDriver ? [filteredDriver] : [];
      } else {
        filteredList = clone(this.list);
      }

      return filteredList;
    },
    sortList() {
      let sortKey;

      switch (this.selectedCard) {
        case 'Trips':
          sortKey = 'total_trips';
          break;
        case 'Ratings':
          sortKey = 'selected_time_rating';
          break;
        default:
          sortKey = 'gross_fare';
          break;
      }

      this.list = clone(orderBy(this.list, [sortKey], ['desc']));
    },
    percentageSign(value) {
      return !isNaN(value) ? '%' : '';
    },
  },
};
</script>
